/**
 * PKCE (RFC 7636) primitives.
 *
 * The console is a public client: the authorization code travels back through
 * the browser's address bar, where anything that can read the URL can steal it.
 * PKCE makes a stolen code useless without the verifier, which never leaves this
 * tab and is never sent to the authorization endpoint — only its hash is.
 *
 * S256 only. The spec permits `plain`, where the "challenge" is the verifier
 * itself; that defeats the entire mechanism and exists for clients without a
 * hash function. Every browser has one.
 */

/** base64url per RFC 4648 section 5: no padding, URL-safe alphabet. */
function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  // crypto.getRandomValues, never Math.random: the state parameter is a CSRF
  // token and the verifier is a one-time secret. Both must be unguessable.
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

/** 32 bytes gives 43 base64url characters, the minimum RFC 7636 allows. */
export function createCodeVerifier(): string {
  return randomBase64Url(32);
}

export function createStateToken(): string {
  return randomBase64Url(16);
}

export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}
